import React, { createContext, useContext, useState, useEffect } from 'react';
import {
    AccountInfo,
    InteractionRequiredAuthError,
} from '@azure/msal-browser';
import { useMsal } from '@azure/msal-react';
import { reset as resetSync } from '../redux/sync.slice';
import { reset as resetTeam } from '../redux/team.slice';
import { reset as resetTimesheets } from '../redux/timeSheet.slice';
import { store } from '../redux';
import { loginRequest } from '../services/msalConfig';

interface AuthContextType {
    account: AccountInfo | null;
    isAuthenticated: boolean;
    isLoading: boolean;
    login: () => Promise<void>;
    logout: () => Promise<void>;
    getToken: () => Promise<string | null>;
    claims: any;
    userName: string | null;
    setAccount: React.Dispatch<React.SetStateAction<AccountInfo | null>>;
    setIsAuthenticated: React.Dispatch<React.SetStateAction<boolean>>;
    setClaims: React.Dispatch<React.SetStateAction<any>>;
    setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
    redirectProcessing: boolean;
}

const AuthContext = createContext<AuthContextType>({
    account: null,
    isAuthenticated: false,
    isLoading: true,
    login: async () => { },
    logout: async () => { },
    getToken: async () => null,
    claims: null,
    userName: null,
    setAccount: () => { },
    setIsAuthenticated: () => { },
    setClaims: () => { },
    setIsLoading: () => { },
    redirectProcessing: false,
});

// Initialize MSAL instance

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [account, setAccount] = useState<AccountInfo | null>(null);
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [claims, setClaims] = useState<any>(null);
    const [userName, setUserName] = useState<string | null>(null);
    const { instance, accounts } = useMsal();
    const [redirectProcessing, setRedirectProcessing] = useState(true);

    useEffect(() => {
        instance.handleRedirectPromise()
            .then((response) => {
                if (response) {
                    instance.setActiveAccount(response.account);
                    setAccount(response.account);
                    setIsAuthenticated(true);
                    setClaims(response.idTokenClaims || response.account?.idTokenClaims || null);


                    const currentAccount = response.account;
                    if (currentAccount) {

                        const name = currentAccount?.name || 'Unknown User';
                        setUserName(name);
                    }

                } else {
                    const existingAccount = instance.getAllAccounts()[0];

                    if (existingAccount) {
                        const name = existingAccount?.name || 'Unknown User';
                        setUserName(name);
                        setAccount(existingAccount);
                        setClaims(existingAccount.idTokenClaims || null);
                        instance.setActiveAccount(existingAccount);
                        setIsAuthenticated(true);
                    } else {
                        setAccount(null);
                        setClaims(null);
                        setUserName(null);
                        setIsAuthenticated(false);
                    }

                }
            })
            .catch((error) => {
                console.error("Redirect error:", error);
            })
            .finally(() => {
                setRedirectProcessing(false);
                setIsLoading(false);
            });
    }, [instance, accounts]);



    useEffect(() => {
        // Initialize authentication state
        const initAuth = async () => {
            try {
                // Check if user is already signed in
                if (accounts.length > 0) {
                    setAccount(accounts[0]);
                    setClaims(accounts[0]?.idTokenClaims || null);

                    const currentAccount = accounts[0];
                    const name = currentAccount.name || 'Unknown User';
                    setUserName(name);
                    // Silently acquire token to verify authentication
                    try {
                        const response = await instance.acquireTokenSilent({
                            scopes: ["User.Read"],
                            account: accounts[0]
                        });

                        setIsAuthenticated(true);
                        setClaims(response.idTokenClaims);
                    } catch (error) {
                        if (error instanceof InteractionRequiredAuthError) {
                            // User needs to login again
                            setIsAuthenticated(false);
                        } else {
                            // Keep cached account session details when offline/token refresh fails.
                            setIsAuthenticated(true);
                            setClaims(accounts[0]?.idTokenClaims || null);
                        }
                    }
                } else {
                    setIsAuthenticated(false);
                    setAccount(null);
                    setClaims(null);
                    setUserName(null);
                }
            } catch (error) {
                console.error("Auth initialization error:", error);
                setIsAuthenticated(false);
            } finally {
                setIsLoading(false);
            }
        };

        initAuth();
    }, [accounts, instance]);

    const login = async () => {
        try {
            setIsLoading(true);

            if (!instance.getAllAccounts().length) {
                instance.loginRedirect({ scopes: loginRequest.scopes });
            }

        } catch (error) {
            console.error("Login error:", error);
        } finally {
            setIsLoading(false);
        }
    };

    const logout = async () => {
        setIsLoading(true);

        // Display confirmation dialog before logout
        if (!window.confirm("Are you sure you want to log out? All stored data will be cleared.")) {
            setIsLoading(false);
            return;
        }

        try {
            await instance.logoutPopup();
            setAccount(null);
            setIsAuthenticated(false);
            setClaims(null);
            // reset redux state or any other state management if needed
            store.dispatch(resetSync());
            store.dispatch(resetTeam());
            store.dispatch(resetTimesheets());
            setUserName(null);
        } catch (error) {
            console.error("Logout error:", error);
        } finally {
            setIsLoading(false);
        }
    };

    const getToken = async (): Promise<string | null> => {
        try {
            if (!account) return null;

            const response = await instance.acquireTokenSilent({
                scopes: ["User.Read"],
                account: account
            });

            return response.accessToken;
        } catch (error) {
            console.error("Token acquisition error:", error);
            return null;
        }
    };

    const value = {
        account,
        isAuthenticated,
        isLoading,
        login,
        logout,
        getToken,
        claims,
        userName,
        setAccount,
        setIsAuthenticated,
        setClaims,
        setIsLoading,
        redirectProcessing
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => useContext(AuthContext);
